import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, CircularProgress, debounce, Grid, Stack, TextField, Typography } from '@mui/material';
import { MediaCard, MediaCardContent, MediaCardName, MediaCardThumbnail } from 'src/UIKit';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCanvaService } from '../services';
import { CanvaDesigns } from '../types/CanvaDesigns';

import { FileLinkWithMimeType } from 'src/constants/types';
import { ConnectToCanvaButton } from './ConnectToCanvaButton';
import { ImageWithFallback, getSafeThumbnailUrl } from 'src/components/ImageWithFallback';

interface CanvaDesignsPreviewProps {
  attachImage: (link: FileLinkWithMimeType, designName?: string) => void;
  handleClose: () => void;
  hideButtons?: boolean;
  onExportStart?: () => void;
  onExportEnd?: (url: string, designName: string) => void;
  onDesignSelect?: (designId: string, designTitle: string) => void;
  selectedDesignId?: string | null;
  selectionMode?: boolean;
}

export const CanvaDesignsPreview: FC<CanvaDesignsPreviewProps> = props => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const {
    onExportStart,
    onExportEnd,
    onDesignSelect,
    selectedDesignId,
    attachImage,
    handleClose,
    selectionMode = false,
  } = props;
  const [designs, setDesigns] = useState<CanvaDesigns | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>();

  const { getDesigns, getDesign, loading, changeCanvaQuery } = useCanvaService();

  const fetchDesigns = async () => {
    const result = await getDesigns();
    if (result) {
      setDesigns(result);
    }
  };

  const fetchWithDelay = () => {
    window.setTimeout(async () => {
      fetchDesigns();
    }, 100);
  };

  const handleDesignClick = async (designId: string, designTitle: string) => {
    if (!selectedCompanyId) {
      return;
    }

    // If in selection mode, just select the design without calling getDesign
    if (selectionMode && onDesignSelect) {
      onDesignSelect(designId, designTitle);
      return;
    }

    // Otherwise, use the old export mode
    onExportStart?.(); // Notify parent that export is starting
    const design = await getDesign(selectedCompanyId, designId, designTitle);
    if (design) {
      attachImage(
        {
          url: design.data.url,
          mimeType: 'image/png',
        },
        designTitle,
      );
      handleClose();

      onExportEnd?.(design.data.url, designTitle); // Notify parent that export is finished
    } else {
      setDesigns(undefined);
    }
  };

  useEffect(() => {
    window.addEventListener('focus', fetchWithDelay);
    fetchDesigns();

    return () => {
      window.removeEventListener('focus', fetchWithDelay);
    };
  }, []);

  const fetchDesignsDebounce = useMemo(() => debounce(fetchDesigns, 500), []);

  useEffect(() => {
    changeCanvaQuery(searchQuery);
    fetchDesignsDebounce();
  }, [searchQuery]);

  return (
    <>
      {designs?.items !== undefined && !loading.getDesign && (
        <Box my={1.25}>
          <TextField
            fullWidth
            disabled={!!(designs?.items?.length && loading.getDesign)}
            label="Search"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </Box>
      )}
      <Box mt={1.25} minHeight={400} maxHeight={400} overflow="auto">
        {!loading.getDesigns && !designs?.items && (
          <Box width="100%" height={70} mt={2.5}>
            <ConnectToCanvaButton disabled={loading.getDesigns} />
          </Box>
        )}
        {loading.getDesigns && (
          <Box height={222}>
            <CircularProgress sx={{ position: 'absolute', left: '50%', top: '50%' }} />
          </Box>
        )}

        {loading.getDesign && (
          <Stack spacing={1.25} justifyContent="center" alignItems="center">
            <Typography>Exporting your design from Canva…</Typography>
            <CircularProgress />
          </Stack>
        )}
        <Box>
          <Grid
            container
            spacing={3}
            paddingTop={1}
            paddingBottom={1}
            paddingLeft={1}
            paddingRight={1}
          >
            {!loading.getDesigns &&
              !(designs?.items?.length && loading.getDesign) &&
              designs?.items?.map((d) => (
                <Grid item xs={12} sm={6} md={4} key={d.id}>
                  <MediaCard cardHeight={150} selected={selectedDesignId === d.id}>
                    <MediaCardContent onClick={() => handleDesignClick(d.id, d.title)}>
                      <MediaCardThumbnail sx={{ height: 100 }}>
                        <Stack flex={1} direction="row" alignItems="center" justifyContent="center">
                          <Box display="flex" maxWidth="100%" borderRadius={1} overflow="hidden">
                            <ImageWithFallback
                              src={getSafeThumbnailUrl(d)}
                              alt={`Thumbnail for ${d.title}`}
                              fallbackText="No preview"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '120px',
                                objectFit: 'contain',
                                display: 'block',
                              }}
                            />
                          </Box>
                        </Stack>
                      </MediaCardThumbnail>
                      <MediaCardName nameGap={20}>
                        <Typography variant="subtitle2" noWrap>
                          {d.title}
                        </Typography>
                      </MediaCardName>
                    </MediaCardContent>
                  </MediaCard>
                </Grid>
              ))}
          </Grid>
        </Box>
      </Box>
    </>
  );
};
